import { AxiosResponse } from 'axios'

import { getStorage } from '@src/utils/enhancedStorage'
import { keyBy, omit } from '@src/utils/functions/utils'

import isBrowser from '../../utils/isBrowser'
import {
  CART_AUTH_CODE_KEY,
  CART_AUTH_CODE_RES_KEY,
  LOCAL_STORAGE_NEW_CART_ID_KEY,
} from '../checkoutService/new-checkout.constants'
import { getInventory } from '../inventoryService/inventory.service'

import { getProductData } from './cart.service'
import { Cart, CartLineItem } from './cart.service.types'

export function setCartAuthCodeFromHeader(res: AxiosResponse) {
  const cartAuthCode = res.headers[CART_AUTH_CODE_RES_KEY] as string
  if (cartAuthCode) {
    getStorage().setItem(CART_AUTH_CODE_KEY, cartAuthCode, { isGlobal: false })
  }
}

export function getCartAuthCodeFromHeader() {
  if (!isBrowser()) return null

  const code = getStorage().getItem(CART_AUTH_CODE_KEY, { isGlobal: false })
  return code
}

export async function getInventoryDataForCart(data: Cart) {
  if (
    data.lineItems?.length === 0 &&
    (!data.saveForLaterItems?.length || data.saveForLaterItems.length === 0) &&
    (!data.outOfStockLineItems?.length || data.outOfStockLineItems.length === 0)
  )
    return {}

  const variantIds = data.lineItems?.map((item: any) => item.variantId) || []
  const saveForLaterVariantIds = data.saveForLaterItems?.map((item: any) => item.variantId) || []
  const outOfStockVariantIds = data.outOfStockLineItems?.map((item: any) => item.variantId) || []
  const allVariantIds = [...variantIds, ...saveForLaterVariantIds, ...outOfStockVariantIds].join(',')
  const inventoryData = await getInventory({ variantIds: allVariantIds })
  return inventoryData
}

export async function getProductDataForCart(data: Cart) {
  if (!isBrowser()) return {}
  const productIdMap =
    data?.lineItems?.reduce((acc: any, lineItem: CartLineItem) => {
      return {
        ...acc,
        [lineItem.variantId]: lineItem?.variantMetaData?.productId,
      }
    }, {}) || {}

  const saveForLaterProductIdMap =
    data?.saveForLaterItems?.reduce((acc: any, lineItem: CartLineItem) => {
      return {
        ...acc,
        [lineItem.variantId]: lineItem?.variantMetaData?.productId,
      }
    }, {}) || {}

  const outOfStockProductIdMap =
    data?.outOfStockLineItems?.reduce((acc: any, lineItem: CartLineItem) => {
      return {
        ...acc,
        [lineItem.variantId]: lineItem?.variantMetaData?.productId,
      }
    }, {}) || {}

  const totalProductIdMap = { ...productIdMap, ...saveForLaterProductIdMap, ...outOfStockProductIdMap }

  const productIds = Object.values(totalProductIdMap)
  const products =
    productIds.length > 0
      ? await getProductData({
          productIds,
          useBatching: true,
        })
      : {}
  const productMap = keyBy(products, 'id')

  const productDataWithVariantMap = Object.entries(totalProductIdMap).reduce(
    (acc: any, [variantId, productId]: any) => {
      return {
        ...acc,
        [variantId]: {
          productData: omit(productMap[productId], [`variants`]),
          selectedVariant: productMap[productId]?.variants?.find((variant: any) => {
            return variant?.id?.toString() === variantId
          }),
        },
      }
    },
    {},
  )
  return productDataWithVariantMap
}

export function mergeInventoryDataInCart(data: Cart, inventoryData: any) {
  const lineItems = data.lineItems.map(item => {
    return {
      ...item,
      inventoryData: inventoryData[item.variantId],
    }
  })

  return {
    ...data,
    lineItems,
  }
}

function mergeProductDataInCart(data: any, _productData: any) {
  const lineItems = data.lineItems.map((lineItem: any) => {
    const variantId = lineItem.variantId
    const product = _productData[variantId]
    const productMetaData = product.selectedVariant
    const slug = product.productData.slug

    return {
      ...lineItem,
      productMetaData,
      slug,
    }
  })

  const saveForLaterlineItems =
    data?.saveForLaterItems?.map((lineItem: any) => {
      const variantId = lineItem.variantId
      const product = _productData[variantId]
      const productMetaData = product?.selectedVariant
      const slug = product?.productData?.slug

      return {
        ...lineItem,
        productMetaData,
        slug,
      }
    }) || []

  const outOfStocklineItems =
    data?.outOfStockLineItems?.map((lineItem: any) => {
      const variantId = lineItem.variantId
      const product = _productData[variantId]
      const productMetaData = product?.selectedVariant
      const slug = product?.productData?.slug

      return {
        ...lineItem,
        productMetaData,
        slug,
      }
    }) || []

  return {
    ...data,
    lineItems: lineItems,
    saveForLaterItems: saveForLaterlineItems,
    outOfStockLineItems: outOfStocklineItems,
  }
}

export async function getDataWithProductAndInventory(data: Cart) {
  try {
    const [productData, inventoryData] = await Promise.all([getProductDataForCart(data), getInventoryDataForCart(data)])
    const inventoryDataWithCart = mergeInventoryDataInCart(data, inventoryData)
    const mergedData = mergeProductDataInCart(inventoryDataWithCart, productData)
    return mergedData
  } catch (error) {
    console.error(error)
    return data
  }
}

export const removeCartIdFromLocalStorage = () => {
  getStorage().removeItem(LOCAL_STORAGE_NEW_CART_ID_KEY, { isGlobal: false, removeAllLocalized: true })
}

export function setCartAuthCodeToLocalStorage(code: string) {
  getStorage().setItem(CART_AUTH_CODE_KEY, code, { isGlobal: false })
}
